import useNotify from 'hooks/useNotify';
import React, { useState } from 'react';
import CopyButton from 'ui/elements/CopyButton';
import Button from 'ui/elements/buttons/Button';
import ButtonList from 'ui/elements/buttons/ButtonList';
import FormRow from 'ui/elements/form/FormRow';
import Label from 'ui/elements/form/Label';
import TextField from 'ui/elements/form/TextField';
import bluePlanetTheme from 'ui/theme/themeBluePlanet';
import { Content, DialogActions } from 'ui/views/dialogs/Dialog';
import i18n from 'util/i18n';
import useLazyResource from 'util/resource/useLazyResource';
import { emailIsValid, emailRegex } from 'util/stringUtils';

function EmailInput({
  autoFocus,
  onBlur,
}: {
  autoFocus: boolean;
  onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}) {
  const [emailInput, setEmailInput] = useState('');
  const [hasBlurred, setHasBlurred] = useState(false);

  const emailError = `invalid email. Please make sure the address is valid, e.g. ${i18n('en').placeholders.email}`;

  return (
    <TextField
      autoFocus={autoFocus}
      error={hasBlurred && emailInput && !emailRegex.test(emailInput) ? true : false}
      helperText={hasBlurred && emailInput && !emailRegex.test(emailInput) ? emailError : ''}
      onBlur={e => {
        setHasBlurred(true);
        onBlur(e);
      }}
      value={emailInput}
      onChange={event => {
        setHasBlurred(false);
        setEmailInput(event.target.value);
      }}
    />
  );
}

export default function InviteConnectionForm({
  onComplete,
  onBack,
  onClose,
  inviteFunction,
  inviteLabelText,
  shareLabelText,
  footerText,
  publicUrl,
  isLastStep,
}: {
  onComplete: () => void;
  onBack: () => void;
  onClose: () => void;
  inviteFunction: (emails: string[]) => Promise<any>;
  inviteLabelText: string;
  shareLabelText: string;
  footerText: string;
  publicUrl: string;
  isLastStep: boolean;
}) {
  const notify = useNotify();
  const [multiline, setMultiline] = useState(false);
  const [emails, setEmails] = useState<string[]>(['', '', '']);

  const onUserEmailPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const list = e.clipboardData
      .getData('text')
      .replace(/[\r\n;\s]+/g, ',')
      .split(',')
      .filter(pastedEmail => !emails.find(existingEmail => existingEmail === pastedEmail) && emailIsValid(pastedEmail));
    setEmails([...emails, ...list]);
    e.preventDefault();
  };

  const [onInviteUsers, isInvitingUsers] = useLazyResource(
    () => {
      const validEmails = emails
        .map(email => email.trim().replace(/,$/, ''))
        .filter(email => email && emailRegex.test(email));

      if (validEmails.length > 500) {
        return Promise.reject('We only support inviting 500 people at a time');
      } else if (validEmails.length > 0) {
        return inviteFunction(validEmails);
      } else {
        return Promise.resolve();
      }
    },
    {
      onSuccess: () => {
        onComplete();
      },
      onFailure: e => notify('error', e || 'Could not invite people'),
    },
  );

  return (
    <>
      <Content>
        <p className="u-content-spacing-bottom">
          Invite your current connections to help you grow, including early angels, board members, key team members,
          advisors, and shareholders. You can also modify these connections later if needed.
        </p>
        <Label>{inviteLabelText}</Label>
        <div>
          {multiline ? (
            <FormRow>
              <TextField
                multiline
                minRows={6}
                maxRows={12}
                autoFocus
                onPaste={onUserEmailPaste}
                helperText="Add email addresses one by one, or paste in a list of addresses seperated by comma or line break"
                value={emails.join('\n')}
                onChange={e => setEmails(e.target.value.split('\n'))}
              />
            </FormRow>
          ) : (
            <>
              {emails.map((email, i) => (
                <FormRow key={i}>
                  <EmailInput
                    onBlur={event => setEmails(emails.map((e, j) => (i === j ? event.target.value : e)))}
                    autoFocus={i === 0}
                  />
                </FormRow>
              ))}
              <Button
                className="u-half-spacing-top"
                kind="tertiary"
                onClick={() => {
                  if (emails.length < 5) {
                    setEmails([...emails, '']);
                  } else setMultiline(true);
                }}
              >
                Add another
              </Button>
            </>
          )}
          <FormRow>
            <Label>{shareLabelText}</Label>
            <div className="u-flex">
              <TextField
                inputProps={{ readOnly: true, backgroundColor: bluePlanetTheme.bluePlanetPalette.grey.light }}
                value={publicUrl}
                className="u-half-spacing-right"
              />
              <CopyButton copyString={publicUrl} displayString="Copy" color="grey" kind="secondary" />
            </div>
          </FormRow>
        </div>
        <div className="u-content-spacing-top text-metadata-regular">{footerText}</div>
      </Content>
      <DialogActions align="space-between">
        <ButtonList>
          <Button isLoading={isInvitingUsers} onClick={() => onInviteUsers('')} kind="primary">
            {isLastStep ? 'Finish' : 'Next'}
          </Button>
          <Button onClick={onBack} kind="primary" color="greyLight">
            Back
          </Button>
        </ButtonList>
        <Button onClick={onClose} kind="tertiary" color="grey">
          Pick it up later
        </Button>
      </DialogActions>
    </>
  );
}
